import { useMutation } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

import type {
  ReferrerBonusEnabledResponse,
  ReferrerBonusResultRow,
} from "./useGetReferrerBonusEnabled";

type StoredToken = {
  AccessToken?: string;
};

export type PatchReferrerBonusEnabledPayload = {
  user_id: string;
  /**
   * Target state of the underlying flag — **not** the toggle's "blocked"
   * state. The UI toggle is named "Block Refer a Friend"; toggle ON sends
   * `referrer_bonus_enabled: false`, toggle OFF sends `true`.
   */
  referrer_bonus_enabled: boolean;
};

/**
 * Re-exported here so callers that only import this hook still get access
 * to the per-row result shape.
 */
export type { ReferrerBonusResultRow };

export type PatchReferrerBonusEnabledResponse = ReferrerBonusEnabledResponse;

const patchReferrerBonusEnabled = async ({
  user_id,
  referrer_bonus_enabled,
}: PatchReferrerBonusEnabledPayload): Promise<PatchReferrerBonusEnabledResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const response = await PrivateAdminPanelInstance.patch(
    API_ROUTES.USER_REFERRER_BONUS_ENABLED.replace("{user_id}", user_id),
    { referrer_bonus_enabled },
  );

  return response?.data ?? null;
};

export const usePatchReferrerBonusEnabled = () =>
  useMutation<
    PatchReferrerBonusEnabledResponse,
    Error,
    PatchReferrerBonusEnabledPayload
  >({
    mutationKey: ["patchReferrerBonusEnabled"],
    mutationFn: patchReferrerBonusEnabled,
  });
